import * as z from 'zod';

export const roleTypeEnum = z.enum(['SuperAdmin', 'Admin', 'Custom']);
export type RoleType = z.infer<typeof roleTypeEnum>;

export const featureKeyEnum = z.enum([
  'api-documentation',
  'cargos',
  'certificates',
  'composition',
  'invoice',
  'project-management',
  'sodir',
  'terminal-configuration',
  'user-management',
]);
export const safeFeatureKeyEnum = featureKeyEnum.catch(ctx => ctx.value as FeatureKey);
export type FeatureKey = z.infer<typeof featureKeyEnum>;

export const permissionKeyEnum = z.enum([
  'api-documentation:read',
  'cargos:read',
  'certificates:read',
  'composition:declare',
  'composition:manage-basis',
  'composition:manage-config',
  'composition:manage-schema',
  'composition:read',
  'invoice:manage',
  'invoice:read',
  'project-management:manage-external-project-references',
  'project-management:read-external-project-references',
  'project-management:manage-projects',
  'project-management:read-projects',
  'sodir:admin',
  'sodir:edit',
  'sodir:read',
  'sodir:submit',
  'terminal-configuration:read',
  'terminal-configuration:write',
  'user-management:manage-roles',
  'user-management:manage-users',
  'user-management:read',
  'user-management:register-user',
]);
export const safePermissionKeyEnum = permissionKeyEnum.catch(ctx => ctx.value as PermissionKey);
export type PermissionKey = z.infer<typeof permissionKeyEnum>;

export const featureScopeEnum = z.enum(['Organization', 'Project']);
export const featureAudienceEnum = z.enum(['Internal', 'External']);
export type FeatureScope = z.infer<typeof featureScopeEnum>;
export type FeatureAudience = z.infer<typeof featureAudienceEnum>;

export type FeatureGate = {
  key: FeatureKey | PermissionKey;
  scope: FeatureScope;
};

export const project = (key: FeatureKey | PermissionKey): FeatureGate => ({ key, scope: 'Project' });
export const organization = (key: FeatureKey | PermissionKey): FeatureGate => ({ key, scope: 'Organization' });

export const permissionSchema = z.object({
  id: z.string(),
  key: safePermissionKeyEnum,
  name: z.string(),
  description: z.string().nullish().transform(data => data ?? ''),
  scope: featureScopeEnum,
  audience: featureAudienceEnum,
});

export const userFeatureSchema = z.object({
  key: safeFeatureKeyEnum,
  name: z.string(),
  description: z.string().nullish().transform(data => data ?? ''),
  scope: featureScopeEnum,
  audience: featureAudienceEnum,
});

export const featureSchema = z.object({
  key: safeFeatureKeyEnum,
  name: z.string(),
  description: z.string().nullish().transform(data => data ?? ''),
  scope: featureScopeEnum,
  audience: featureAudienceEnum,
  permissions: permissionSchema.array().nullish().transform(data => data ?? []),
});

const roleBaseSchema = z.object({
  id: z.uuidv4(),
  name: z.string(),
  description: z.string(),
  permissions: permissionSchema.array().nullish().transform(data => data ?? []),
  type: roleTypeEnum,
  userCount: z.int(),
});

export const roleSchema = roleBaseSchema.extend({
  organizationId: z.string(),
});

export const projectRoleSchema = roleBaseSchema.extend({
  projectId: z.string(),
});

export const roleInfoSchema = z.object({
  id: z.string().nullish().transform(data => data ?? ''),
  type: roleTypeEnum,
});

export const createRoleSchema = z.object({
  name: z.string().nonempty('Role name is required'),
  description: z.string().nonempty('Role description is required'),
  permissionIds: z.string().array().nonempty('A role must have at least one permission'),
});

export const registerPersonSchema = z.object({
  email: z.email({ error: 'A valid email is required' }),
});

export const registerServiceUserSchema = z.object({
  enterpriseObjectId: z.string().trim().min(1, 'Enterprise object ID is required'),
  name: z.string().nullish().transform(data => data?.trim() ?? ''),
});

const rolesSchema = roleInfoSchema.array().nullish().transform(data => data ?? []);

export const personUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  roles: rolesSchema,
});

export const serviceUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  objectId: z.string().nullish(),
  roles: rolesSchema,
});

export const registerPersonResponseSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
}).transform(data => ({
  ...data,
  roles: [],
}));

export const registerServiceUserResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
}).transform(data => ({
  ...data,
  roles: [],
}));

export function pagedResultSchema<T extends z.ZodType>(itemSchema: T) {
  return z.object({
    items: itemSchema.array().nullish().transform(data => data ?? []),
    pageNumber: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
    totalPages: z.number(),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
  });
}

export const pagedPersonUsersSchema = pagedResultSchema(personUserSchema);
export const pagedServiceUsersSchema = pagedResultSchema(serviceUserSchema);

export const userMeSchema = z.object({
  email: z.string(),
  name: z.string(),
  organizationId: z.string(),
  id: z.uuidv4().or(z.string()),
});

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  features: userFeatureSchema.array().nullish().transform(data => data ?? []),
  permissions: permissionSchema.array().nullish().transform(data => data ?? []),
});

export const projectSchema = z.object({
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  features: userFeatureSchema.array().nullish().transform(data => data ?? []),
  permissions: permissionSchema.array().nullish().transform(data => data ?? []),
});

export const organizationProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
});

export type PagedResult<T> = {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export type UserMe = z.infer<typeof userMeSchema>;
export type PersonUser = z.infer<typeof personUserSchema>;
export type ServiceUser = z.infer<typeof serviceUserSchema>;
export type ManagedSubject = {
  id: string;
  name: string;
  roles: z.infer<typeof roleInfoSchema>[];
  email?: string;
};
export type Role = z.infer<typeof roleSchema>;
export type ProjectRole = z.infer<typeof projectRoleSchema>;
export type RoleView = z.infer<typeof roleBaseSchema>;
export type Feature = z.infer<typeof featureSchema>;
export type UserFeature = z.infer<typeof userFeatureSchema>;
export type Permission = z.infer<typeof permissionSchema>;
export type CreateRoleBody = z.infer<typeof createRoleSchema>;
export type RegisterPersonBody = z.infer<typeof registerPersonSchema>;
export type RegisterServiceUserBody = z.infer<typeof registerServiceUserSchema>;
export type RegisterPersonResponse = z.infer<typeof registerPersonResponseSchema>;
export type RegisterServiceUserResponse = z.infer<typeof registerServiceUserResponseSchema>;
export type Project = z.infer<typeof projectSchema>;
export type Organization = z.infer<typeof organizationSchema>;
export type OrganizationProject = z.infer<typeof organizationProjectSchema>;
